import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { createYupSchema } from "../../../utilities/yupSchemaCreator";
import { CarFields } from "../Fields";
import styled from "styled-components";
import { CarCategoryType } from "../../../utilities/Enum";
import { useCar } from "../../Car/Provider";
import {
  Alert,
  Box,
  Button,
  Card,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import ErrorsList from "../../../ui/ErrorsList";
import { useNavigate, useParams } from "react-router-dom";
import { useMember } from "../provider";

const ButtonWrapper = styled.div`
  margin-bottom: 50px;
  button {
    float: right;
    margin-left: 20px;
  }
`;

const FormCar = ({ Car, handleSuccess }) => {
  let theme = useTheme();
  const navigate = useNavigate();
  const { mid } = useParams();

  const [error, setError] = useState();
  const plateRegexRef = useRef(null);

  const regexToExample = (regex, lower = false) => {
  try {
    return regex
      .replace(/^\^/, "")
      .replace(/\$$/, "")
      .replace(/\[A-Za-z\]/g, lower ? "a" : "A")
      .replace(/\\d\{(\d+)\}/g, (_, n) => "1".repeat(parseInt(n)))
      .replace(/\\d\+/g, "12345")
      .replace(/\\d/g, "1")
      .replace(/\+/g, "")
      .replace(/\^/g, "")
      .replace(/\$/g, "");
  } catch (e) {
    return regex;
  }
};

const resolver = useCallback(async (values, context, options) => {
    const base = CarFields.reduce(createYupSchema, {});
    if (plateRegexRef.current && base.plate) {
      try {
        const regex = new RegExp(plateRegexRef.current);
        const upperExample = regexToExample(plateRegexRef.current, false);
        const lowerExample = regexToExample(plateRegexRef.current, true);
        const example =
          upperExample !== lowerExample
            ? `${upperExample} or ${lowerExample}`
            : upperExample;
        base.plate = base.plate.matches(
          regex,
          `Plate format is invalid. Example: ${example}`,
        );
      } catch (e) {
        // invalid regex, skip validation
      }
    }
    const formSchema = Yup.object().shape(base);
    return yupResolver(formSchema)(values, context, options);
  }, []);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver,
  });
  const {
    state: { selected: member },
    createCar,
    editCar,
  } = useMember();
  const {
    state: { listColor: colors, listMaker: makers },
    fetchListColor: getColors,
    fetchListMaker: getMakers,
  } = useCar();
  useEffect(() => {
    getColors();
  }, [getColors]);
  useEffect(() => {
    getMakers();
  }, [getMakers]);
  useEffect(() => {
    if (member?.company?.plateRegularExpression) {
      plateRegexRef.current = member.company.plateRegularExpression;
    }
  }, [member]);
  const getData = (name) => {
    const dataMap = {
      carMakerId: [
        ...(makers?.map(({ id, makerName }) => ({
          id: id,
          name: makerName,
        })) || []),
      ],
      categoryId: [
        ...(CarCategoryType?.map(({ id, name }) => ({
          id: id,
          name: name,
        })) || []),
      ],
      carColorId: [
        ...(colors?.map(({ id, colorName }) => ({
          id: id,
          name: colorName,
        })) || []),
      ],
    };

    return dataMap[name] || [];
  };
  useEffect(() => {
    setValue("memberId", mid);
  }, [setValue, mid]);
  useEffect(() => {
    if (Car) {
      CarFields.forEach((f) => {
        if (f.name !== "memberId") setValue(f.name, Car?.[f.name]);
      });
    }
  }, [Car, setValue]);
  const onSubmit = async (values) => {
    try {
      if (Car) await editCar(values, Car?.id);
      else await createCar(values);
      handleSuccess();
    } catch (error) {
      setError(error.errors.message);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      {error ? (
        <Alert severity="error" sx={{ my: 3 }}>
          <ErrorsList error={error} />
        </Alert>
      ) : (
        ""
      )}
      <Card
        sx={{
          p: theme.dimensions.padding,
          mt: theme.dimensions.mt,
          mb: theme.dimensions.mb,
        }}
      >
        <Typography variant="Poster" mb={3} component="h4" gutterBottom>
          Car Details
        </Typography>
        <Grid container spacing={2} sx={{ width: "80%" }}>
          {CarFields.map((f, index) => (
            <Grid key={index} item xs={12} md={6}>
              {f.name === "memberId" && (
                <Box>
                  <span style={{ fontSize: "12px" }}>Member Name</span>
                  <Box
                    sx={{
                      borderBottom: "1px solid gray",
                      width: "290px",
                      borderRadius: "8px",
                      textAlign: "center",
                      marginRight: "20px",
                    }}
                  >
                    {member?.memberName}
                  </Box>
                </Box>
              )}
              {f.name !== "memberId" && (
                <>
                  {f.switch ? (
                    <FormGroup>
                      <FormLabel
                        component="legend"
                        sx={{
                          mt: theme.dimensions.mt,
                        }}
                      >
                        {f.label}
                      </FormLabel>
                      <FormControlLabel
                        sx={{
                          mt: theme.dimensions.mt,
                        }}
                        control={<Switch color="success" defaultChecked />}
                        {...register(f?.name)}
                        label="Active"
                      />
                    </FormGroup>
                  ) : (
                    <TextField
                      label={f.label}
                      name={f.name}
                      error={!!errors[f.name]}
                      helperText={errors[f.name]?.message}
                      select={f.select}
                      className={f.class}
                      type={f.type}
                      fullWidth
                      disabled={f.disabled}
                      required={f.required}
                      defaultValue={Car?.[f.name]}
                      sx={{
                        width: theme.dimensions.width,
                        mt: theme.dimensions.mt,
                        mb: theme.dimensions.mb,
                      }}
                      inputProps={{
                        ...register(f?.name),
                        required: f.required,
                      }}
                      size="small"
                    >
                      {getData(f.name)?.map((item, index) => (
                        <MenuItem key={index} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </>
              )}
            </Grid>
          ))}
        </Grid>
      </Card>
      <ButtonWrapper>
        <Button
          variant="contained"
          type="submit"
          sx={{
            backgroundColor: theme.color.blackBackground,
            ml: theme.dimensions.ml,
            "&:hover": { background: `${theme.color.black}` },
          }}
        >
          {Car ? "Save" : " Add Car"}
        </Button>
        <Button
          variant="contained"
          onClick={() => navigate(-1)}
          sx={{
            backgroundColor: theme.color.grayBackground,
            color: theme.color.dark,
            "&:hover": { background: `${theme.color.gray50}` },
          }}
        >
          Discard
        </Button>
      </ButtonWrapper>
    </form>
  );
};

export default FormCar;
